import { useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import {
  ADMIN_DEFAULT_TIME_RANGE_DAYS,
  ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  type AdminProjectionFreshness,
  type AdminReleaseRegressionQuery,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import { loadReleaseRegression } from "./releases-use-cases";
import {
  toReleaseRegressionViewModel,
  type ReleaseRegressionViewModel,
} from "./release-regression-view-model";

export type ReleaseRegressionScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  query?: AdminReleaseRegressionQuery;
};

export type ReleaseRegressionScreenModel = AdminScreenState<ReleaseRegressionViewModel>;

function createDefaultReleaseRegressionQuery(): AdminReleaseRegressionQuery {
  const now = new Date();

  return {
    timeRange: {
      from: new Date(now.getTime() - ADMIN_DEFAULT_TIME_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      to: now.toISOString(),
    },
    limit: ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  };
}

function deriveFreshnessPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "stale" | "partial" | "ready" {
  const values = Object.values(sourceFreshness);

  if (values.includes("stale")) {
    return "stale";
  }

  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }

  return "ready";
}

function createDependenciesKey(
  dependencies: ReleaseRegressionScreenModelDependencies,
): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    query: dependencies.query ?? null,
  });
}

export async function resolveReleaseRegressionScreenModel(
  dependencies: ReleaseRegressionScreenModelDependencies,
): Promise<ReleaseRegressionScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.releases.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "Release regression read permission denied.",
        ko: "릴리스 회귀 조회 권한이 없습니다.",
      },
      "admin_releases_permission_denied",
    );
  }

  try {
    const envelope = await loadReleaseRegression(
      dependencies.queryPort,
      dependencies.query ?? createDefaultReleaseRegressionQuery(),
    );

    if (envelope.data.items.length === 0) {
      return {
        status: "empty",
      };
    }

    const viewModel = toReleaseRegressionViewModel(
      envelope.data,
      envelope.meta,
      dependencies.locale ?? "ko",
    );
    const freshnessPriority = deriveFreshnessPriority(envelope.meta.sourceFreshness);

    if (freshnessPriority === "stale") {
      return {
        status: "stale",
        viewModel,
      };
    }

    if (freshnessPriority === "partial") {
      return {
        status: "partial",
        viewModel,
      };
    }

    return {
      status: "ready",
      viewModel,
    };
  } catch (error) {
    return toAdminScreenErrorState(dependencies.locale ?? "ko", error);
  }
}

export function useReleaseRegressionScreenModel(
  dependencies: ReleaseRegressionScreenModelDependencies,
): ReleaseRegressionScreenModel {
  const [state, setState] = useState<ReleaseRegressionScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);

  useEffect(() => {
    let cancelled = false;

    setState((current) => createLoadingAdminScreenState(current.viewModel));

    void resolveReleaseRegressionScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependenciesKey]);

  return state;
}
